import { useCallback, useState } from "react";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { processError } from "../../../../common/utils/base_error";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { DashboardCompras } from "../../domain/models/dashboard_compras";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { initialRequisicaoServicosProdutosListaFilters, useRequisicaoServicosProdutosListaFilters } from "../hooks/RequisicaoServicosProdutos_lista/filters";
import { initialOrdemComprasListaFilters, useOrdemComprasListaFilters } from "../hooks/OrdemCompras_lista/filters";
import { initialFornecedoresListaFilters, useFornecedoresListaFilters } from "../hooks/Fornecedores_lista/filters";
import { initialColetaPrecosListaFilters, useColetaPrecosListaFilters } from "../hooks/ColetaPrecos_lista/filters";
import { PrincipalDocumentosLista } from "../../../auditoria/domain/models/documentos_lista";
import { useAuditoresPagination } from "../../../auditoria/presentation/hooks/Auditoria_auditores/pagination";
import { useRequisicaoServicosProdutosListaPagination } from "../hooks/RequisicaoServicosProdutos_lista/pagination";
import { useOrdemComprasListaPagination } from "../hooks/OrdemCompras_lista/pagination";
import { useFornecedoresListaPagination } from "../hooks/Fornecedores_lista/pagination";
import { useColetaPrecosListaPagination } from "../hooks/ColetaPrecos_lista/pagination";

import CommonConstants from "../../../../common/constants";

/**
 * LoadPrincipalListaFuncState
 */

type LoadPrincipalListaFuncState = {
  IdArquivoModulo?: number;
  page?: number;
  rowsPerPage?: number;
  tipoPesquisa?: number,
  pesquisa?: string,
};

/**
 * usePrincipalComprasViewModel
 */

export const usePrincipalComprasViewModel = () => {
  const {
    commons: {
      getAllListagemDocUseCase,
      getGedArquivoDataUseCase,
    },
    compras: {
      getDashboardComprasUseCase,
    },
  } = useDependencyInjection();

  const { pushErrorNotification } = usePageLoader();

  const backOrNavigate = useBackOrNavigate();

  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const [dashboardComprasInformation, setDashboardComprasInformation] = useState<DashboardCompras>();

  const [docLista, setDocLista] = useState<PrincipalDocumentosLista[]>([]);

  const pagination = useAuditoresPagination();

  /**
   * Funções de carregamento
   */

  const loadDashboardCompras = useCallback(async () => {
    try {
      const comprasInfo = await getDashboardComprasUseCase.getDashboardCompras();
      setDashboardComprasInformation(comprasInfo);
    } catch (e: unknown) {
      setDashboardComprasInformation(undefined);
      throw e;
    }
  }, [
    getDashboardComprasUseCase,
  ]);

  const loadDocListaState = useCallback(async (funcState?: LoadPrincipalListaFuncState) => {
    try {
      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
      await getAllListagemDocUseCase.getAllListagemDoc(
        String(CommonConstants.moduloDocumentos.Compras),
        filteredPage,
        filteredRowsPerPage
      );
      setDocLista(getAllListagemDocUseCase.items);
      pagination.setRowsPerPage(getAllListagemDocUseCase.pageSize);
      pagination.setTotalItems(getAllListagemDocUseCase.totalItems);
    } catch (e: unknown) {
      throw new Error("Falha ao buscar documentos!");
    }
  }, [
    getAllListagemDocUseCase,
    pagination,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      await loadDashboardCompras();
      await loadDocListaState();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao carregar os dados!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadDashboardCompras();
      await loadDocListaState();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao recarregar os dados!"));
    } finally {
      setLoading(false);
    }
  }, [
    loadDashboardCompras,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRelatorio = useCallback(() => {
    try {
      setLoading(true);
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, "Falha ao buscar relatório!"));
    } finally {
      setLoading(false);
    }
  }, [
    pushErrorNotification,
  ]);

  const handleCardClick = useCallback((cardType: string) => {
    switch (cardType) {
      case 'requisicoesPendentes':
        useRequisicaoServicosProdutosListaFilters.setState({ ...initialRequisicaoServicosProdutosListaFilters, status: 2 });
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        backOrNavigate('/RequisicaoServicosProdutos_lista');
        break;

      case 'requisicoesEmAtraso':
        useRequisicaoServicosProdutosListaFilters.setState({ ...initialRequisicaoServicosProdutosListaFilters, status: 6 });
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        backOrNavigate('/RequisicaoServicosProdutos_lista');
        break;

      case 'requisicoesAprovadas':
        useRequisicaoServicosProdutosListaFilters.setState({ ...initialRequisicaoServicosProdutosListaFilters, status: 3 });
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        backOrNavigate('/RequisicaoServicosProdutos_lista');
        break;

      case 'requisicoesReprovadas':
        useRequisicaoServicosProdutosListaFilters.setState({ ...initialRequisicaoServicosProdutosListaFilters, status: 4 });
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        backOrNavigate('/RequisicaoServicosProdutos_lista');
        break;

      case 'requisicoesFinalizadas':
        useRequisicaoServicosProdutosListaFilters.setState({ ...initialRequisicaoServicosProdutosListaFilters, status: 5 });
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        backOrNavigate('/RequisicaoServicosProdutos_lista');
        break;

      case 'requisicoesRevisar':
        useRequisicaoServicosProdutosListaFilters.setState({ ...initialRequisicaoServicosProdutosListaFilters, status: 8 });
        useRequisicaoServicosProdutosListaPagination.setState({ page: 1 });
        backOrNavigate('/RequisicaoServicosProdutos_lista');
        break;

      case 'ordemCompraPendentes':
        useOrdemComprasListaFilters.setState({ ...initialOrdemComprasListaFilters, status: [2] });
        useOrdemComprasListaPagination.setState({ page: 1 });
        backOrNavigate('/OrdemCompras_lista');
        break;

      case 'ordemCompraEntregues':
        useOrdemComprasListaFilters.setState({ ...initialOrdemComprasListaFilters, status: [3] });
        useOrdemComprasListaPagination.setState({ page: 1 });
        backOrNavigate('/OrdemCompras_lista');
        break;

      case 'ordemCompraCanceladas':
        useOrdemComprasListaFilters.setState({ ...initialOrdemComprasListaFilters, status: [5] });
        useOrdemComprasListaPagination.setState({ page: 1 });
        backOrNavigate('/OrdemCompras_lista');
        break;

      case 'documentosVencidos':
        useFornecedoresListaFilters.setState({ ...initialFornecedoresListaFilters, documento: 2 });
        useFornecedoresListaPagination.setState({ page: 1 });
        backOrNavigate('/Fornecedores_lista');
        break;

      case 'coletasEmElaboracao':
        useColetaPrecosListaFilters.setState({ ...initialColetaPrecosListaFilters, status: 0 });
        useColetaPrecosListaPagination.setState({ page: 1 });
        backOrNavigate('/ColetaPrecos_lista');
        break;

      case 'coletasFinalizadas':
        useColetaPrecosListaFilters.setState({ ...initialColetaPrecosListaFilters, status: 1 });
        useColetaPrecosListaPagination.setState({ page: 1 });
        backOrNavigate('/ColetaPrecos_lista');
        break;

      case 'coletasAprovadas':
        useColetaPrecosListaFilters.setState({ ...initialColetaPrecosListaFilters, status: 3 });
        useColetaPrecosListaPagination.setState({ page: 1 });
        backOrNavigate('/ColetaPrecos_lista');
        break;

      case 'coletasReprovadas':
        useColetaPrecosListaFilters.setState({ ...initialColetaPrecosListaFilters, status: 4 });
        useColetaPrecosListaPagination.setState({ page: 1 });
        backOrNavigate('/ColetaPrecos_lista');
        break;

      case 'coletasGerouOrdemCompra':
        useColetaPrecosListaFilters.setState({ ...initialColetaPrecosListaFilters, status: 2 });
        useColetaPrecosListaPagination.setState({ page: 1 });
        backOrNavigate('/ColetaPrecos_lista');
        break;

      case 'mediaGeralAprovacaoFornecedores':
        useFornecedoresListaFilters.setState({ ...initialFornecedoresListaFilters });
        useFornecedoresListaPagination.setState({ page: 1 });
        backOrNavigate('/Fornecedores_lista');
        break;

      case 'entregaOrdemCompraAtraso':
        useOrdemComprasListaFilters.setState({ ...initialOrdemComprasListaFilters, status: [6] });
        useOrdemComprasListaPagination.setState({ page: 1 });
        backOrNavigate('/OrdemCompras_lista');
        break;
    }
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      pagination.setPage(page + 1);;
      await loadDocListaState({ page: page + 1, });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      pagination.setPage(1);
      pagination.setRowsPerPage(rowsPerPage);

      await loadDocListaState({ page: 1, rowsPerPage });
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [
    pagination,
    loadDocListaState,
    pushErrorNotification,
  ]);

  const handleDownloadFile = useCallback(async (IdArquivoModulo: number, NomeArquivo: string) => {
    if (IdArquivoModulo > 0) {
      try {
        setLoading(true);
        await getGedArquivoDataUseCase
          .getGedArquivoData(IdArquivoModulo, NomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar documentos!'));
      } finally {
        setLoading(false);
      }
    }
  }, [
    getGedArquivoDataUseCase,
    pushErrorNotification,
  ]);

  return {
    loading,
    errorMessage,
    dashboardComprasInformation: dashboardComprasInformation ?? null,
    docLista,
    pagination,
    handleReload,
    handleRelatorio,
    handleCardClick,
    handlePageChange,
    handleRowsPerPageChange,
    handleDownloadFile,
  };
}